// ✅ src/screens/UpgradeScreen.js (FULL)
// Paystack Inline (React 19 friendly — NO react-paystack install)
//
// ✅ Requires:
// 1) public/index.html includes:
//    <script src="https://js.paystack.co/v1/inline.js"></script>
//
// 2) .env (project root) contains:
//    REACT_APP_PAYSTACK_KEY=pk_test_xxx   (or pk_live_xxx)
//    Restart `npm start` after editing .env
//
// ✅ FIXES/UPDATES:
// ✅ Uses NEW collection: appSubscriptions (so it won’t mix with your other site)
// ✅ Auto-creates subscription doc on successful payment
// ✅ Saves ACTIVE subscription with 30 days period
// ✅ Keeps payment history in payments/{ref}

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

/* ---------------- responsive helper ---------------- */
function useIsSmall(maxWidth = 640) {
  const [isSmall, setIsSmall] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);

    const onChange = (e) => setIsSmall(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    setIsSmall(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [maxWidth]);

  return isSmall;
}

function useIsMedium(min = 641, max = 980) {
  const [isMedium, setIsMedium] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(min-width:${min}px) and (max-width:${max}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(min-width:${min}px) and (max-width:${max}px)`);

    const onChange = (e) => setIsMedium(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    setIsMedium(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [min, max]);

  return isMedium;
}

/* ---------------- money helper ---------------- */
function planAmountKobo(planKey) {
  // Paystack expects smallest unit: 30.00 => 3000
  if (planKey === "Plus") return 3000; // GHS 30
  if (planKey === "Pro") return 5000; // GHS 50
  if (planKey === "VIP") return 7000; // GHS 70
  return 0;
}

/* ---------------- date helper ---------------- */
function addDaysFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
}

/* ---------------- UI bits ---------------- */
function Pill({ children }) {
  return <span style={S.pill}>{children}</span>;
}

function PlanCard({ title, price, perks, highlight, selected, onSelect, tag, compact }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        ...S.planCard,
        ...(compact ? S.planCardCompact : null),
        ...(highlight ? S.planHighlight : null),
        ...(selected ? S.planSelected : null),
      }}
      aria-label={`Select ${title}`}
    >
      <div style={S.planTop}>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...S.planTitle, ...(compact ? S.planTitleCompact : null) }}>
            {title}
          </div>
          <div style={{ ...S.planPrice, ...(compact ? S.planPriceCompact : null) }}>
            {price}
          </div>
        </div>

        <Pill>{tag || (highlight ? "Best value" : "Popular")}</Pill>
      </div>

      <div style={S.planPerks}>
        {perks.map((p) => (
          <div key={p} style={S.perkRow}>
            <span style={S.perkDot}>✓</span>
            <span style={S.perkText}>{p}</span>
          </div>
        ))}
      </div>

      <div style={S.planCta}>{selected ? "Selected" : "Choose plan"}</div>
    </button>
  );
}

export default function UpgradeScreen() {
  const nav = useNavigate();

  const isSmall = useIsSmall(640);
  const isMedium = useIsMedium(641, 980);

  const [choice, setChoice] = useState("Pro");
  const [me, setMe] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setMe(u || null));
    return () => unsub();
  }, []);

  const plans = useMemo(
    () => [
      {
        key: "Plus",
        title: "Plus",
        price: "GHS 30 / month",
        highlight: false,
        tag: "Starter",
        perks: ["Unlimited messaging", "See who likes you", "No ads"],
      },
      {
        key: "Pro",
        title: "Pro",
        price: "GHS 50 / month",
        highlight: true,
        tag: "Best value",
        perks: [
          "Everything in Plus",
          "3 profile boosts / week",
          "Exclusive badge",
          "Advanced filters",
          "Meetups access (Limited: 5 chats / week)",
        ],
      },
      {
        key: "VIP",
        title: "VIP",
        price: "GHS 70 / month",
        highlight: false,
        tag: "Elite",
        perks: [
          "Everything in Pro",
          "Meetups access (Unlimited)",
          "Boost daily (or 10 boosts / week)",
          "VIP badge + top placement",
        ],
      },
    ],
    []
  );

  const selectedPlan = useMemo(
    () => plans.find((p) => p.key === choice) || plans[0],
    [choice, plans]
  );

  const gridCols = isSmall
    ? "1fr"
    : isMedium
    ? "repeat(2, minmax(0, 1fr))"
    : "repeat(3, minmax(0, 1fr))";

  async function startPaystack() {
    setError("");

    if (!me?.uid) {
      setError("Please sign in first.");
      return;
    }

    const publicKey = String(process.env.REACT_APP_PAYSTACK_KEY || "").trim();
    if (!publicKey) {
      setError("Missing Paystack key. Add REACT_APP_PAYSTACK_KEY in .env and restart npm start.");
      return;
    }

    if (!window?.PaystackPop) {
      setError("Paystack not loaded. Make sure public/index.html has the script tag, then restart.");
      return;
    }

    const amount = planAmountKobo(selectedPlan.key);
    if (!amount) {
      setError("Invalid plan amount.");
      return;
    }

    const email = (me?.email || "").trim() || "customer@email.com";
    const reference = `sub_${selectedPlan.key}_${me.uid}_${Date.now()}`;

    setPaying(true);

    // ✅ async work OUTSIDE callback
    const handleSuccess = async (ref) => {
      try {
        // 1) Save payment history
        await setDoc(doc(db, "payments", ref), {
          uid: me.uid,
          email,
          plan: selectedPlan.key,
          priceLabel: selectedPlan.price,
          amount,
          currency: "GHS",
          reference: ref,
          status: "success_callback",
          createdAt: serverTimestamp(),
        });

        // 2) ✅ Save THIS APP subscription (separate from your other site)
        //    Collection: appSubscriptions/{uid}
        await setDoc(
          doc(db, "appSubscriptions", me.uid),
          {
            uid: me.uid,
            plan: selectedPlan.key,         // Plus | Pro | VIP
            status: "active",               // ✅ active (simple mode)
            currentPeriodStart: serverTimestamp(),
            currentPeriodEnd: addDaysFromNow(30),
            lastPaymentRef: ref,
            priceLabel: selectedPlan.price,
            amount,
            currency: "GHS",
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );

        alert(`Payment received ✅\nReference: ${ref}`);
      } catch (e) {
        console.error(e);
        setError("Payment succeeded but saving failed. Check Firestore rules.");
      } finally {
        setPaying(false);
      }
    };

    try {
      const handler = window.PaystackPop.setup({
        key: publicKey,
        email,
        amount,
        currency: "GHS",
        ref: reference,

        callback: function (response) {
          const ref = String(response?.reference || reference);
          handleSuccess(ref);
        },

        onClose: function () {
          setPaying(false);
        },

        metadata: {
          uid: me.uid,
          plan: selectedPlan.key,
        },
      });

      handler.openIframe();
    } catch (e) {
      console.error(e);
      setPaying(false);
      setError("Failed to start payment. Check console for details.");
    }
  }

  return (
    <div style={{ ...S.page, ...(isSmall ? S.pageSmall : null) }}>
      <div style={{ ...S.wrap, ...(isSmall ? S.wrapSmall : null) }}>
        <div style={{ ...S.topBar, ...(isSmall ? S.topBarSmall : null) }}>
          <button type="button" onClick={() => nav(-1)} style={S.backBtn}>
            ← Back
          </button>

          <div style={{ minWidth: 0 }}>
            <div style={S.title}>Upgrade</div>
            <div style={S.sub}>Pick a plan that unlocks more features.</div>
          </div>
        </div>

        <div style={{ ...S.hero, ...(isSmall ? S.heroSmall : null) }}>
          <div style={S.heroIcon}>⭐</div>
          <div style={S.heroText}>
            <div style={S.heroTitle}>Go Premium</div>
            <div style={S.heroSub}>More visibility, faster matches, and better control.</div>
          </div>
        </div>

        <div style={{ ...S.plansGrid, gridTemplateColumns: gridCols }}>
          {plans.map((p) => (
            <PlanCard
              key={p.key}
              title={p.title}
              price={p.price}
              perks={p.perks}
              highlight={p.highlight}
              selected={choice === p.key}
              onSelect={() => setChoice(p.key)}
              tag={p.tag}
              compact={isSmall}
            />
          ))}
        </div>

        {error ? (
          <div style={S.errBox}>
            <div style={S.errTitle}>Heads up</div>
            <div style={S.errText}>{error}</div>
          </div>
        ) : null}

        <div style={{ ...S.checkout, ...(isSmall ? S.checkoutSmall : null) }}>
          <div style={S.checkoutLeft}>
            <div style={S.checkoutTitle}>Selected</div>
            <div style={S.checkoutValue}>
              {selectedPlan.title} • {selectedPlan.price}
            </div>
            <div style={S.checkoutTiny}>
              Paying as: <b>{me?.email || "No email (Auth)"}</b>
            </div>
          </div>

          <button
            type="button"
            style={{
              ...S.payBtn,
              ...(isSmall ? S.payBtnSmall : null),
              ...(paying ? S.payBtnDisabled : null),
            }}
            onClick={startPaystack}
            disabled={paying}
          >
            {paying ? "Opening Paystack..." : "Continue"}
          </button>
        </div>

        <div style={S.note}>
          Tip: Later you can verify Paystack ref on backend before keeping subscription active.
        </div>
      </div>
    </div>
  );
}

/* ✅ styles (unchanged) */
const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background:
      "radial-gradient(1200px 650px at 18% 0%, rgba(236,72,153,.14), transparent 60%), " +
      "radial-gradient(900px 520px at 90% 18%, rgba(99,102,241,.14), transparent 55%), " +
      "linear-gradient(180deg,#fbfaf8 0%, #f7f2ee 55%, #fbf7f3 100%)",
    color: "#0f172a",
  },
  pageSmall: { padding: 12 },

  wrap: {
    width: "min(980px, calc(100% - 24px))",
    margin: "0 auto",
    display: "grid",
    gap: 14,
  },
  wrapSmall: { width: "100%" },

  topBar: {
    borderRadius: 22,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.08)",
    backdropFilter: "blur(12px)",
    padding: 14,
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  topBarSmall: { padding: 12 },

  backBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 12px 28px rgba(2,6,23,0.08)",
    flexShrink: 0,
  },

  title: { fontWeight: 1000, fontSize: 18, letterSpacing: -0.3 },
  sub: { fontWeight: 900, opacity: 0.65, fontSize: 13 },

  hero: {
    borderRadius: 24,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.70)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.08)",
    backdropFilter: "blur(12px)",
    padding: 14,
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  heroSmall: { padding: 12 },

  heroIcon: {
    width: 52,
    height: 52,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    fontSize: 22,
    fontWeight: 1000,
    background: "linear-gradient(135deg, rgba(236,72,153,.18), rgba(99,102,241,.16))",
    border: "1px solid rgba(236,72,153,.18)",
    flexShrink: 0,
  },
  heroText: { minWidth: 0 },
  heroTitle: { fontWeight: 1000, fontSize: 18, letterSpacing: -0.2 },
  heroSub: { fontWeight: 900, opacity: 0.65, marginTop: 4, fontSize: 13 },

  plansGrid: { display: "grid", gap: 12 },

  planCard: {
    textAlign: "left",
    borderRadius: 22,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 16px 40px rgba(2,6,23,0.08)",
    padding: 14,
    cursor: "pointer",
    display: "grid",
    gap: 12,
  },
  planCardCompact: { padding: 12 },
  planHighlight: {
    border: "1px solid rgba(236,72,153,.20)",
    boxShadow: "0 20px 60px rgba(236,72,153,.10)",
  },
  planSelected: { outline: "3px solid rgba(99,102,241,.18)" },

  planTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "flex-start",
  },
  planTitle: { fontWeight: 1000, fontSize: 18, letterSpacing: -0.3 },
  planTitleCompact: { fontSize: 17 },
  planPrice: { fontWeight: 900, opacity: 0.75, marginTop: 6 },
  planPriceCompact: { fontSize: 13 },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 1000,
    fontSize: 12,
    background: "rgba(255,255,255,0.75)",
    border: "1px solid rgba(2,6,23,0.08)",
    whiteSpace: "nowrap",
  },

  planPerks: { display: "grid", gap: 8 },
  perkRow: { display: "flex", gap: 10, alignItems: "center" },
  perkDot: {
    width: 20,
    height: 20,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    fontWeight: 1000,
    background: "rgba(99,102,241,.12)",
    border: "1px solid rgba(99,102,241,.18)",
    flexShrink: 0,
  },
  perkText: { fontWeight: 900, opacity: 0.8, lineHeight: 1.2 },

  planCta: {
    marginTop: 2,
    borderRadius: 16,
    padding: "10px 12px",
    fontWeight: 1000,
    textAlign: "center",
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.80)",
  },

  checkout: {
    borderRadius: 24,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.70)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.08)",
    backdropFilter: "blur(12px)",
    padding: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  checkoutSmall: { padding: 12 },

  checkoutLeft: { display: "grid", gap: 4 },
  checkoutTitle: { fontWeight: 900, opacity: 0.65, fontSize: 12 },
  checkoutValue: { fontWeight: 1000, fontSize: 16 },
  checkoutTiny: { fontWeight: 900, opacity: 0.65, fontSize: 12, marginTop: 4 },

  payBtn: {
    border: "none",
    borderRadius: 999,
    padding: "12px 16px",
    cursor: "pointer",
    fontWeight: 1000,
    color: "#fff",
    background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))",
    boxShadow: "0 14px 34px rgba(99,102,241,0.25)",
  },
  payBtnSmall: { width: "100%", padding: "12px 14px" },
  payBtnDisabled: { opacity: 0.7, cursor: "not-allowed" },

  note: { fontWeight: 900, opacity: 0.6, fontSize: 12, textAlign: "center" },

  errBox: {
    borderRadius: 18,
    border: "1px solid rgba(239,68,68,0.20)",
    background: "rgba(239,68,68,0.06)",
    padding: 12,
  },
  errTitle: { fontWeight: 1000, marginBottom: 4 },
  errText: { fontWeight: 900, opacity: 0.8, fontSize: 13 },
};
